import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StagePill, StatusPill, TypePill, ArchiveBadge } from "@/components/pills";
import { fmtHM, fmtDate, fmtTime } from "@/lib/format";
import { TIME_FIELDS, PRIORITY_STYLES, liveStageSeconds, liveTotalSeconds, useNow } from "@/lib/workflow";
import { canEditCreative } from "@/lib/roles";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, History, Clock, ExternalLink, Save, ArrowRight, Trash2, FolderOpen } from "lucide-react";
import { toast } from "sonner";

export default function WorkItemDetail({ asset, open, onOpenChange, config, users, user, canApprove, isAdmin, onChanged }) {
  useNow(1000);
  const [edit, setEdit] = useState({});

  useEffect(() => {
    if (asset) {
      setEdit({
        work_item: asset.work_item || asset.title || "",
        brief: asset.brief || asset.description || "",
        work_category: asset.work_category || asset.type || "",
        sub_category: asset.sub_category || "",
        instruction_from: asset.instruction_from || "",
        work_lead_id: asset.work_lead_id || asset.owner_id || "",
        priority: asset.priority || "Medium",
        due_date: asset.due_date || "",
        next_action: asset.next_action || "",
        drive_source_link: asset.drive_source_link || "",
        file_path: asset.file_path || "",
      });
    }
  }, [asset]);

  if (!asset) return null;

  const saveFields = async () => {
    await api.patch(`/assets/${asset.asset_id}`, edit);
    toast.success("Work item updated");
    onChanged?.();
  };

  const saveSource = async () => {
    try {
      await api.patch(`/assets/${asset.asset_id}`, { drive_source_link: edit.drive_source_link, file_path: edit.file_path });
      toast.success("Source link & file path saved");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const canLink = canEditCreative(user, asset);
  const isComplete = asset.current_stage === "Complete";
  const isTodo = asset.current_stage === "To Do";
  const isOwner = (asset.work_lead_id || asset.owner_id) === user?.user_id;
  // Only Admin edits master fields. Reviewer / Final Approver never edit the work item.
  const canEditMaster = isAdmin;
  // Drive Source Link: editable by Admin or the Creative Owner during production
  // stages. Reviewer / Final Approver only get a read-only "Open Source" link.
  const canEditLink = isAdmin || canLink;
  // File Path / Location lives ONLY in the COMPLETE → archive context.
  const canEditArchiveFields = isAdmin || (isComplete && (isOwner || canApprove));

  const setArchive = async (archive_status) => {
    await api.post(`/assets/${asset.asset_id}/archive-status`, { archive_status });
    toast.success(`Archive status: ${archive_status}`);
    onChanged?.();
  };

  const history = [...(asset.workflow_history || [])].reverse();
  const stageElapsed = liveStageSeconds(asset);
  const total = liveTotalSeconds(asset);
  const subOptions = config.subcategories?.[edit.work_category] || [];
  const activeUsers = (users || []).filter((u) => u.status !== "inactive");
  const adminUsers = activeUsers.filter((u) => (u.roles || []).includes("ADMIN"));

  const deleteAsset = async () => {
    if (!window.confirm(`Delete ${asset.work_item_id} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${asset.asset_id}`);
      toast.success("Work item deleted");
      onOpenChange?.(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const Row = ({ label, value }) => (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-0.5">{value || "—"}</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 flex-wrap">
            <span className="font-mono text-primary">{asset.work_item_id}</span>
            <span>{asset.work_item || asset.title}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <StagePill stage={asset.current_stage} />
          {isComplete && <ArchiveBadge archiveStatus={asset.archive_status} />}
          {isComplete && <StatusPill status={asset.archive_status} />}
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${PRIORITY_STYLES[asset.priority] || ""}`}>{asset.priority}</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtHM(stageElapsed)} in {asset.current_stage}</span>
          {!isAdmin && !canEditMaster && !canLink && <span className="ml-auto text-[11px] text-muted-foreground">Read-only · only an admin can edit</span>}
          {!isAdmin && canLink && <span className="ml-auto text-[11px] text-teal-600 dark:text-teal-400 font-medium">You can update the Google Drive source link</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
          {/* Details */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Details</p>
            </div>
            {canEditMaster ? (
              <>
                <div><Label className="text-xs">Work Item</Label>
                  <Input data-testid="detail-work-item-input" value={edit.work_item} onChange={(e) => setEdit({ ...edit, work_item: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Brief / Requirements</Label>
                  <Textarea data-testid="detail-brief-input" value={edit.brief || ""} onChange={(e) => setEdit({ ...edit, brief: e.target.value })} className="mt-1" rows={2} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Work Category</Label>
                    <Select value={edit.work_category} onValueChange={(v) => setEdit({ ...edit, work_category: v, sub_category: (config.subcategories?.[v]?.[0] || "") })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{(config.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Sub-category</Label>
                    <Select value={edit.sub_category} onValueChange={(v) => setEdit({ ...edit, sub_category: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{subOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Instruction From</Label>
                    <Select value={edit.instruction_from} onValueChange={(v) => setEdit({ ...edit, instruction_from: v })}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{adminUsers.map((u) => <SelectItem key={u.user_id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Priority</Label>
                    <Select value={edit.priority} onValueChange={(v) => setEdit({ ...edit, priority: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{(config.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Creative Owner</Label>
                    <Select value={edit.work_lead_id} onValueChange={(v) => setEdit({ ...edit, work_lead_id: v })}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{activeUsers.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div><Label className="text-xs">Due Date</Label>
                    <Input type="date" value={edit.due_date || ""} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} className="mt-1" /></div>
                </div>
                <div><Label className="text-xs">Next Action</Label>
                  <Input data-testid="detail-next-action-input" value={edit.next_action || ""} onChange={(e) => setEdit({ ...edit, next_action: e.target.value })} className="mt-1" /></div>
                <Button data-testid="detail-save-button" onClick={saveFields} size="sm" className="gap-1.5"><Save className="h-4 w-4" /> Save</Button>
                {isAdmin && (
                  <Button data-testid="detail-delete-button" onClick={deleteAsset} size="sm" variant="outline" className="gap-1.5 ml-2 text-rose-600 border-rose-300 hover:bg-rose-500/10">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                )}
              </>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div className="col-span-2"><Row label="Brief / Requirements" value={asset.brief || asset.description} /></div>
                <Row label="Work Category" value={asset.work_category || asset.type} />
                <Row label="Sub-category" value={asset.sub_category} />
                <Row label="Instruction From" value={asset.instruction_from} />
                <Row label="Creative Owner" value={asset.work_lead_name || asset.owner_name} />
                <Row label="Created By" value={asset.created_by_name} />
                <Row label="Due Date" value={asset.due_date ? fmtDate(asset.due_date) : "—"} />
                <Row label="Next Action" value={asset.next_action} />
              </div>
            )}
          </div>

          {/* System / time */}
          <div className="space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Stage Time</p>
              <div className="grid grid-cols-2 gap-2">
                {TIME_FIELDS.map(([key, label]) => (
                  <div key={key} className="rounded-lg border border-border p-2.5">
                    <p className="text-[11px] text-muted-foreground">{label}</p>
                    <p className="font-mono font-semibold text-sm">{fmtHM(asset.times?.[key] || 0)}</p>
                  </div>
                ))}
                <div className="rounded-lg border border-primary/40 bg-primary/5 p-2.5 col-span-2">
                  <p className="text-[11px] text-muted-foreground">Total Time</p>
                  <p className="font-mono font-bold text-base">{fmtHM(total)}</p>
                </div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Created {fmtDate(asset.created_at)} by {asset.created_by_name || "—"}</p>
              {asset.completed_at && <p>Completed {fmtDate(asset.completed_at)} by {asset.completed_by}</p>}
            </div>

            {/* Google Drive source — shown from CREATE onward (not in To Do or Prepare,
                and the archive section owns it at COMPLETE); editable only by
                Admin / Creative Owner, read-only "Open Source" link for Reviewer & Approver */}
            {["Create", "Review", "Approval"].includes(asset.current_stage) && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Google Drive Source Link</Label>
                  {canEditLink ? (
                    <>
                      <div className="flex gap-2 mt-1">
                        <Input data-testid="drive-source-input" value={edit.drive_source_link || ""} onChange={(e) => setEdit({ ...edit, drive_source_link: e.target.value })} placeholder="https://drive.google.com/…" />
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1">Required to send this work to REVIEW. Must be a valid Google Drive URL.</p>
                      <Button data-testid="save-source-button" onClick={saveSource} size="sm" className="gap-1.5 mt-2"><Save className="h-4 w-4" /> Save Source</Button>
                    </>
                  ) : asset.drive_source_link ? (
                    <p className="mt-1"><a data-testid="open-source-link" href={asset.drive_source_link} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1 font-medium"><ExternalLink className="h-3.5 w-3.5" /> Open Source</a></p>
                  ) : (
                    <p className="text-sm text-rose-600 mt-1">Source link missing</p>
                  )}
                </div>
              </div>
            )}

            {/* ARCHIVE — only relevant once Complete */}
            {isComplete && (
              <div data-testid="archive-section" className="rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Archive</p>
                {isAdmin ? (
                  <div>
                    <Label className="text-xs">Archive Status</Label>
                    <Select value={asset.archive_status} onValueChange={setArchive}>
                      <SelectTrigger data-testid="archive-status-select" className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{(config.complete_archive_statuses || config.archive_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : (
                  <Row label="Archive Status" value={asset.archive_status} />
                )}
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Google Drive Source</p>
                  {canEditArchiveFields ? (
                    <Input data-testid="drive-source-input" value={edit.drive_source_link || ""} onChange={(e) => setEdit({ ...edit, drive_source_link: e.target.value })} className="mt-1" placeholder="https://drive.google.com/…" />
                  ) : asset.drive_source_link ? (
                    <a data-testid="open-source-link" href={asset.drive_source_link} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1"><ExternalLink className="h-3.5 w-3.5" /> Open Source</a>
                  ) : <p className="text-sm text-rose-600">Source link missing</p>}
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1"><FolderOpen className="h-3 w-3" /> File Path / Location</p>
                  {canEditArchiveFields ? (
                    <>
                      <Input data-testid="file-path-input" value={edit.file_path || ""} onChange={(e) => setEdit({ ...edit, file_path: e.target.value })} className="mt-1" placeholder="e.g. \\ServerName\LiveLife Education\Creative Assets\2026\September" />
                      <p className="text-[11px] text-muted-foreground mt-1">Stored exactly as entered. Required for full archiving.</p>
                    </>
                  ) : asset.file_path ? (
                    <p className="text-sm font-mono break-all" data-testid="archive-file-path">{asset.file_path}</p>
                  ) : <p className="text-sm text-rose-600">File path missing</p>}
                </div>
                {canEditArchiveFields && (
                  <Button data-testid="save-source-button" onClick={saveSource} size="sm" className="gap-1.5"><Save className="h-4 w-4" /> Save Source &amp; Path</Button>
                )}
                <Row label="Completed Date" value={asset.completed_at ? fmtDate(asset.completed_at) : "—"} />
              </div>
            )}
          </div>
        </div>

        {/* Workflow history */}
        <div className="mt-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Workflow History</p>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">No transitions yet — this item is still in {asset.current_stage}.</p>
          ) : (
            <div className="space-y-0">
              {history.map((h, i) => (
                <div key={`${h.at || ""}-${h.from_stage}-${h.to_stage}-${i}`} data-testid={`history-row-${i}`} className="flex items-start gap-3 py-2 border-b border-border last:border-0">
                  <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium">{h.from_stage}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-medium">{h.to_stage}</span>
                      {h.seconds_in_prev ? <span className="text-xs text-muted-foreground">· {fmtHM(h.seconds_in_prev)} in {h.from_stage}</span> : null}
                    </p>
                    <p className="text-xs text-muted-foreground">{h.changed_by_name} · {fmtDate(h.at)} {fmtTime(h.at)}{h.reason ? ` · ${h.reason}` : ""}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
